import { VideoTile } from "@vc/views/vcLayout/components/video/VideoTile"
import { MobileStageLayout } from "@vc/views/vcLayout/components/MobileStageLayout"
import { ZoomPanVideo } from "@vc/views/vcLayout/components/video/ZoomPanVideo"
import { Maximize, Minimize, ZoomIn, ZoomOut } from "lucide-react"
import type { Participant } from "@vc/models"
import type { BackgroundImage } from "@vc/data/backgroundCategories"
import type { RefObject } from "react"
import { useState, useCallback, useEffect, useReducer, useRef } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { zoomReducer, FIT_STATE, type ZoomMetrics } from "@/features/meeting/services/screen-share-zoom"
import { useFullscreen } from "@vc/viewmodels/screen/useFullscreen"
import { cn } from "@vc/lib/utils"

interface ScreenShareLayoutProps {
  screenVideoRef: RefObject<HTMLVideoElement | null>
  /**
   * Attaches the presented stream to the video element as it mounts (and sets
   * `screenVideoRef`). Without it the element relied on an effect keyed on the
   * stream, which had already run when this layout mounted — after a PiP
   * window closed, or when the layout switched in mid-share — so the presenter
   * saw a black stage.
   */
  attachVideo?: (el: HTMLVideoElement | null) => void
  participants: Participant[]
  /** Emoji for one participant, or null. Per-peer: a reaction
   *  belongs to whoever sent it, not to every tile on screen. */
  reactionFor: (peerId: string | undefined | null) => string | null
  pinnedIds: string[]
  onPin: (participantId: string) => void
  /** Moderator only: pin a tile on everyone's screen. */
  onPinForAll?: (participantId: string) => void
  pinnedForAllId?: string | null
  onMaximize: (element: HTMLElement) => void
  onOpenChat: (participantId: string) => void
  onOpenParticipants?: () => void
  selectedBackground: BackgroundImage | null
  presenterName?: string
  /**
   * Ends whatever is on the stage. Absent when the viewer may not end it.
   *
   * Not necessarily the viewer's OWN share — a moderator gets this for someone
   * else's too — so `isOwnShare` is what decides the wording, not this.
   */
  onStopPresenting?: () => void
  /**
   * Is the share on the stage this viewer's own?
   *
   * Gates the "You are sharing your screen" banner, which used to key off
   * `onStopPresenting` alone. That was the same thing while only a presenter
   * could stop a share; now that a moderator can stop somebody else's, it
   * would announce that they were sharing a screen they are only watching.
   */
  isOwnShare?: boolean
  isLandscape?: boolean // NEW: For landscape row layout
  /**
   * Identifies which share is on the stage, independent of the video
   * element's own `loadedmetadata` (which ZoomPanVideo already treats as "a
   * new source arrived" and resets zoom for). Belt-and-suspenders: covers a
   * share swap that does not re-trigger that event, e.g. the same
   * MediaStream object having its track replaced in place.
   */
  shareKey?: string | null
  /** Tiles the strip shows without scrolling — see MobileStageLayout. */
  onVisibleCountChange?: (count: number) => void
}

/**
 * Screen share on a phone: the screen fills the stage and everyone else is a
 * scrollable strip of tiles (MobileStageLayout). It used to give the screen
 * 40% of the height and a grid the rest, with everyone past three folded into
 * an "N others" tile — the shared screen was the smallest thing on it.
 */
export function ScreenShareLayout({
  screenVideoRef,
  attachVideo,
  participants,
  reactionFor,
  pinnedIds,
  onPin,
  onPinForAll,
  pinnedForAllId,
  onMaximize,
  onOpenChat,
  selectedBackground,
  onStopPresenting,
  isOwnShare = true,
  isLandscape = false, // Default to portrait column layout
  shareKey = null,
  onVisibleCountChange,
}: ScreenShareLayoutProps) {
  const t = useTranslation();
  const [{ zoomLevel, isAtNaturalSize }, dispatchZoom] = useReducer(zoomReducer, FIT_STATE)
  const [zoomMetrics, setZoomMetrics] = useState<ZoomMetrics | null>(null)
  const [isScreenActive, setIsScreenActive] = useState(true) // Track if screen sharing is active
  const stageRef = useRef<HTMLDivElement>(null)
  
  
  // Monitor video element to detect when screen sharing stops
  useEffect(() => {
    const video = screenVideoRef.current
    if (!video) return

    const checkVideoStatus = () => {
      // Check if video has a valid stream and is playing
      const hasStream = video.srcObject !== null
      const isPlaying = !video.paused && video.readyState >= 2
      setIsScreenActive(hasStream && isPlaying)
    }

    // Check initially
    checkVideoStatus()

    // Add event listeners
    video.addEventListener('play', checkVideoStatus)
    video.addEventListener('pause', checkVideoStatus)
    video.addEventListener('emptied', checkVideoStatus) // Fires when srcObject is cleared
    video.addEventListener('loadeddata', checkVideoStatus)

    // Also check periodically in case stream ends without events
    const interval = setInterval(checkVideoStatus, 1000)

    return () => {
      video.removeEventListener('play', checkVideoStatus)
      video.removeEventListener('pause', checkVideoStatus)
      video.removeEventListener('emptied', checkVideoStatus)
      video.removeEventListener('loadeddata', checkVideoStatus)
      clearInterval(interval)
    }
  }, [screenVideoRef])

  // The stage, never the <video>: fullscreening the video on iPhone handed it
  // to the native player — its own pause/resume, and a minimise button that
  // floated the share off the page and left "screen sharing has stopped" in
  // its place. useFullscreen pins the stage in-page where the API is missing,
  // and follows exits made without our button (back gesture, Esc).
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(stageRef)

  // Zoom in
  const zoomIn = useCallback(() => dispatchZoom({ type: 'zoomIn' }), [])

  // Zoom out
  const zoomOut = useCallback(() => dispatchZoom({ type: 'zoomOut' }), [])

  // A different share landing (or this one restarting) must not keep the
  // last one's zoom — see shareKey's doc comment on why both this and
  // ZoomPanVideo's own onSourceChange exist.
  useEffect(() => {
    dispatchZoom({ type: 'shareChanged' })
  }, [shareKey])

  // The presenter resized their window/display mid-share: follow it so "100%"
  // stays exactly 100%, but only while already pinned there — see
  // zoomReducer's metricsUpdated case for why a manual zoom is left alone.
  useEffect(() => {
    if (!zoomMetrics || !isAtNaturalSize) return
    dispatchZoom({ type: 'metricsUpdated', naturalZoom: zoomMetrics.naturalZoom })
  }, [zoomMetrics, isAtNaturalSize])

  
  const stage = (
      <div ref={stageRef} className="relative h-full w-full overflow-hidden rounded-xl bg-gray-900 group">
        {/* Screen video */}
        <ZoomPanVideo
          zoom={zoomLevel}
          videoRef={attachVideo ?? screenVideoRef}
          onMetricsChange={setZoomMetrics}
          onSourceChange={() => dispatchZoom({ type: 'shareChanged' })}
          autoPlay
          playsInline
          muted
        />

        {/* Full black overlay when screen sharing is stopped */}
        {!isScreenActive && (
          <div className="absolute inset-0 bg-black flex flex-col items-center justify-end pb-6 sm:pb-8 z-10">
            <span className="text-xs sm:text-sm font-medium text-white/80">{t("screenShare.yourScreenSharingStopped")}</span>
          </div>
        )}

        {/* "You are sharing screen" banner. Not while full screen: the controls
            move into this corner then (see below). */}
        {onStopPresenting && isOwnShare && !isFullscreen && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs sm:text-sm font-medium text-white">
              {t("screenShare.youAreSharingScreen")}
            </span>
          </div>
        )}

        {/* Screen controls overlay - bottom center - always visible on mobile.
            Top-left while full screen: on iPhone the stage then reaches the
            bottom of the screen, where Safari's toolbar floats over the page
            and the home indicator sits, so the exit button there could be
            neither seen nor tapped. The top edge is always clear. */}
        <div
          className={cn(
            "absolute z-20 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1.5 opacity-100",
            !isFullscreen && "bottom-2 left-1/2 -translate-x-1/2",
          )}
          style={isFullscreen ? {
            top: "max(12px, env(safe-area-inset-top))",
            left: "max(12px, env(safe-area-inset-left))",
          } : undefined}
        >
          {/* Zoom Out */}
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoomLevel <= 0.5}
            className="grid h-8 w-8 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors disabled:opacity-40"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>

          {/* Zoom In */}
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoomLevel >= 3}
            className="grid h-8 w-8 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors disabled:opacity-40"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>


          {/* Fullscreen Toggle — presenter and viewers alike */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="grid h-8 w-8 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors"
            title={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
            aria-label={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
          >
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </button>
        </div>

        {/* Stop presenting button - top right */}
        {onStopPresenting && (
          <div
            className="absolute top-3 right-3 z-20"
            style={isFullscreen ? {
              top: "max(12px, env(safe-area-inset-top))",
              right: "max(12px, env(safe-area-inset-right))",
            } : undefined}
          >
            <button
              type="button"
              onClick={onStopPresenting}
              className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>{isOwnShare ? t("screenShare.stopSharing") : t("videoTile.stopScreenshare")}</span>
            </button>
          </div>
        )}
      </div>
  )

  return (
    <MobileStageLayout
      stage={stage}
      participants={participants}
      isLandscape={isLandscape}
      onVisibleCountChange={onVisibleCountChange}
      renderTile={(participant) => (
        <VideoTile
          participant={participant}
          reaction={reactionFor(participant?.id)}
          isPinned={pinnedIds.includes(participant.id)}
          onPin={onPin}
          onPinForAll={onPinForAll}
          pinnedForAllId={pinnedForAllId}
          onMaximize={onMaximize}
          onOpenChat={onOpenChat}
          virtualBackgroundUrl={participant.isLocal ? selectedBackground?.url ?? null : null}
        />
      )}
    />
  )
}
